import { useCallback, useEffect, useState } from 'react'

import { normalizeWorkflowSpec, type RecurringTaskProposal } from '@/features/recurring/workflow'
import { apiFetch } from '@/lib/api'

const API_BASE = import.meta.env.VITE_API_URL || 'https://sineapi-production-8db6.up.railway.app'

export interface RecurringTask extends RecurringTaskProposal {
  id: string
  userId: string
  isActive: boolean
  autoApproved: boolean
  source: 'agent' | 'manual'
  createdAt: string
  updatedAt: string
  startsAt: string
}

const WORKFLOW_SPEC_PREFIX = '[SINE_WORKFLOW_SPEC]'

function encodeAgentPrompt(prompt: string, workflowSpec?: RecurringTaskProposal['workflowSpec']): string {
  if (!workflowSpec) return prompt
  return `${WORKFLOW_SPEC_PREFIX}${JSON.stringify(workflowSpec)}\n${prompt}`
}

function decodeAgentPrompt(rawPrompt: string): {
  prompt: string
  workflowSpec?: RecurringTaskProposal['workflowSpec']
} {
  if (!rawPrompt.startsWith(WORKFLOW_SPEC_PREFIX)) {
    return { prompt: rawPrompt }
  }

  const newlineIndex = rawPrompt.indexOf('\n')
  if (newlineIndex === -1) {
    return { prompt: rawPrompt }
  }

  const specBlock = rawPrompt.slice(WORKFLOW_SPEC_PREFIX.length, newlineIndex).trim()
  const prompt = rawPrompt.slice(newlineIndex + 1)
  try {
    return {
      prompt,
      workflowSpec: JSON.parse(specBlock) as RecurringTaskProposal['workflowSpec'],
    }
  } catch {
    return { prompt }
  }
}

export interface TaskExecution {
  id: string
  taskId: string
  scheduledFor: string
  startedAt?: string
  finishedAt?: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped'
  resultSummary?: string
  errorMessage?: string
  creditsUsed: number
  triggeredBy: 'scheduler' | 'manual'
  createdAt: string
  workflowKind?: string
  workflowActivationMode?: 'auto_run' | 'manual_review'
  workflowPhase?: string
  workflowState?: Record<string, unknown>
  executionLog?: Record<string, unknown>
  triggerRunId?: string
  triggerRun?: TaskTriggerRun | null
  retryCount?: number
  parentExecutionId?: string
  lastRecoveryAction?: string
}

export interface TaskTriggerRun {
  id: string
  triggerId?: string
  status: 'running' | 'completed' | 'failed' | 'skipped'
  agentRunId?: string
  errorMessage?: string
  startedAt?: string
  completedAt?: string
  durationMs?: number
  inputData?: Record<string, unknown>
  outputData?: Record<string, unknown>
  executionId?: string
  taskId?: string
  workflowKind?: string
}

export interface TaskWorkflowHistorySummary {
  taskId?: string
  workflowKind?: string
  workflowActivationMode?: 'auto_run' | 'manual_review'
  isActive?: boolean
  latestExecution?: TaskExecution | null
  latestTriggerRun?: TaskTriggerRun | null
  counts?: {
    total_executions: number
    successful_executions: number
    failed_executions: number
    pending_executions: number
    total_trigger_runs: number
    failed_trigger_runs: number
  }
  recentErrors?: Array<{
    type: string
    id?: string
    message?: string
    timestamp?: string
  }>
}

export interface TaskWorkflowTimelineEvent {
  id: string
  type: string
  timestamp: string
  label: string
  description: string
  execution_id?: string
  trigger_run_id?: string
  task_id?: string
  workflow_kind?: string
  status?: string
}

export interface TaskRunResponse {
  executionId: string | null
  taskTitle: string
  runMode: 'safe' | 'power'
}

async function calendarApiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  return apiFetch(`${API_BASE}/calendar${path}`, options)
}

export function categoryColor(name: string): string {
  const PRESET: Record<string, string> = {
    'Annonsering': '#1A93FE',
    'Innhold': '#22C55E',
    'Rapportering': '#A855F7',
    'E-post': '#F59E0B',
    'Sosiale medier': '#EC4899',
    'Analyse': '#06B6D4',
    'Møteforberedelse': '#F97316',
    'Kundeoppfølging': '#EF4444',
    'Vedlikehold': '#6B7280',
    'Tilpasset': '#8B5CF6',
  }
  if (PRESET[name]) return PRESET[name]
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 65%, 55%)`
}

function mapTaskRow(row: Record<string, unknown>): RecurringTask {
  const decodedPrompt = decodeAgentPrompt((row.agent_prompt as string) ?? '')
  const baseTask = {
    description: (row.description as string) ?? '',
    connectorName: row.connector_name as string | undefined,
    frequency: row.frequency as RecurringTask['frequency'],
    customDays: row.custom_days as number[] | undefined,
    dayOfWeek: row.day_of_week as number | undefined,
    dayOfMonth: row.day_of_month as number | undefined,
    timeOfDay: row.time_of_day as string,
  }
  return {
    id: row.id as string,
    userId: row.user_id as string,
    title: row.title as string,
    description: baseTask.description,
    category: row.category as string,
    color: row.color as string,
    frequency: baseTask.frequency,
    customDays: baseTask.customDays,
    dayOfWeek: baseTask.dayOfWeek,
    dayOfMonth: baseTask.dayOfMonth,
    timeOfDay: baseTask.timeOfDay,
    connectorId: row.connector_id as string | undefined,
    connectorName: baseTask.connectorName,
    agentPrompt: decodedPrompt.prompt,
    endsAt: row.ends_at as string | undefined,
    workflowSpec: normalizeWorkflowSpec(baseTask, decodedPrompt.workflowSpec),
    isActive: row.is_active as boolean,
    autoApproved: row.auto_approved as boolean,
    source: row.source as 'agent' | 'manual',
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    startsAt: row.starts_at as string,
  }
}

function mapExecutionRow(row: Record<string, unknown>): TaskExecution {
  return {
    id: row.id as string,
    taskId: row.task_id as string,
    scheduledFor: row.scheduled_for as string,
    startedAt: row.started_at as string | undefined,
    finishedAt: row.finished_at as string | undefined,
    status: row.status as TaskExecution['status'],
    resultSummary: row.result_summary as string | undefined,
    errorMessage: row.error_message as string | undefined,
    creditsUsed: (row.credits_used as number | null) ?? 0,
    triggeredBy: row.triggered_by as TaskExecution['triggeredBy'],
    createdAt: row.created_at as string,
    workflowKind: row.workflow_kind as string | undefined,
    workflowActivationMode: row.workflow_activation_mode as TaskExecution['workflowActivationMode'],
    workflowPhase: row.workflow_phase as string | undefined,
    workflowState: row.workflow_state as Record<string, unknown> | undefined,
    executionLog: row.execution_log as Record<string, unknown> | undefined,
    triggerRunId: row.trigger_run_id as string | undefined,
    triggerRun: row.trigger_run ? mapTriggerRunRow(row.trigger_run as Record<string, unknown>) : null,
    retryCount: (row.retry_count as number | null) ?? 0,
    parentExecutionId: row.parent_execution_id as string | undefined,
    lastRecoveryAction: row.last_recovery_action as string | undefined,
  }
}

function mapTriggerRunRow(row: Record<string, unknown>): TaskTriggerRun {
  return {
    id: row.id as string,
    triggerId: row.trigger_id as string | undefined,
    status: row.status as TaskTriggerRun['status'],
    agentRunId: row.agent_run_id as string | undefined,
    errorMessage: row.error_message as string | undefined,
    startedAt: row.started_at as string | undefined,
    completedAt: row.completed_at as string | undefined,
    durationMs: row.duration_ms as number | undefined,
    inputData: row.input_data as Record<string, unknown> | undefined,
    outputData: row.output_data as Record<string, unknown> | undefined,
    executionId: row.execution_id as string | undefined,
    taskId: row.task_id as string | undefined,
    workflowKind: row.workflow_kind as string | undefined,
  }
}

export function useRecurringTasks(userId: string | null) {
  const [tasks, setTasks] = useState<RecurringTask[]>([])
  const [executionsByTask, setExecutionsByTask] = useState<Record<string, TaskExecution[]>>({})
  const [workflowHistoryByTask, setWorkflowHistoryByTask] = useState<Record<string, TaskWorkflowHistorySummary>>({})
  const [workflowTimelineByTask, setWorkflowTimelineByTask] = useState<Record<string, TaskWorkflowTimelineEvent[]>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchExecutions = useCallback(async (taskIds?: string[]) => {
    if (!userId) return
    const ids = taskIds?.filter(Boolean) ?? tasks.map(task => task.id)
    if (ids.length === 0) {
      setExecutionsByTask({})
      return
    }

    try {
      const grouped: Record<string, TaskExecution[]> = {}
      const responses = await Promise.all(
        ids.map(async taskId => {
          const res = await calendarApiFetch(`/tasks/${taskId}/executions?limit=20`)
          const payload = await res.json().catch(() => ({}))
          if (!res.ok) {
            throw new Error((payload as { detail?: string }).detail ?? 'Kunne ikke hente kjøringer')
          }
          return {
            taskId,
            rows: (payload as { executions?: Record<string, unknown>[] }).executions ?? [],
          }
        })
      )

      for (const { taskId, rows } of responses) {
        grouped[taskId] = rows.map(row => mapExecutionRow(row))
      }
      setExecutionsByTask(grouped)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [tasks, userId])

  const fetchWorkflowHistory = useCallback(async (taskIds?: string[]) => {
    if (!userId) return
    const ids = taskIds?.filter(Boolean) ?? tasks.map(task => task.id)
    if (ids.length === 0) {
      setWorkflowHistoryByTask({})
      return
    }

    try {
      const grouped: Record<string, TaskWorkflowHistorySummary> = {}
      const responses = await Promise.all(
        ids.map(async taskId => {
          const res = await calendarApiFetch(`/tasks/${taskId}/history?limit=12`)
          const payload = await res.json().catch(() => ({}))
          if (!res.ok) {
            throw new Error((payload as { detail?: string }).detail ?? 'Kunne ikke hente workflow-historikk')
          }
          return {
            taskId,
            summary: (payload as { summary?: Record<string, unknown> }).summary ?? {},
          }
        }),
      )

      for (const { taskId, summary } of responses) {
        grouped[taskId] = {
          taskId: summary.task_id as string | undefined,
          workflowKind: summary.workflow_kind as string | undefined,
          workflowActivationMode: summary.workflow_activation_mode as TaskWorkflowHistorySummary['workflowActivationMode'],
          isActive: summary.is_active as boolean | undefined,
          latestExecution: summary.latest_execution ? mapExecutionRow(summary.latest_execution as Record<string, unknown>) : null,
          latestTriggerRun: summary.latest_trigger_run ? mapTriggerRunRow(summary.latest_trigger_run as Record<string, unknown>) : null,
          counts: summary.counts as TaskWorkflowHistorySummary['counts'],
          recentErrors: (summary.recent_errors as TaskWorkflowHistorySummary['recentErrors']) ?? [],
        }
      }
      setWorkflowHistoryByTask(grouped)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [tasks, userId])

  const fetchWorkflowTimeline = useCallback(async (taskIds?: string[]) => {
    if (!userId) return
    const ids = taskIds?.filter(Boolean) ?? tasks.map(task => task.id)
    if (ids.length === 0) {
      setWorkflowTimelineByTask({})
      return
    }

    try {
      const grouped: Record<string, TaskWorkflowTimelineEvent[]> = {}
      const responses = await Promise.all(
        ids.map(async taskId => {
          const res = await calendarApiFetch(`/tasks/${taskId}/timeline?limit=18`)
          const payload = await res.json().catch(() => ({}))
          if (!res.ok) {
            throw new Error((payload as { detail?: string }).detail ?? 'Kunne ikke hente workflow-timeline')
          }
          return {
            taskId,
            rows: ((payload as { timeline?: Record<string, unknown>[] }).timeline ?? []).map(row => ({
              id: row.id as string,
              type: row.type as string,
              timestamp: row.timestamp as string,
              label: row.label as string,
              description: row.description as string,
              execution_id: row.execution_id as string | undefined,
              trigger_run_id: row.trigger_run_id as string | undefined,
              task_id: row.task_id as string | undefined,
              workflow_kind: row.workflow_kind as string | undefined,
              status: row.status as string | undefined,
            })),
          }
        }),
      )

      for (const { taskId, rows } of responses) {
        grouped[taskId] = rows
      }
      setWorkflowTimelineByTask(grouped)
      setError(null)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [tasks, userId])

  const fetchTasks = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    try {
      const res = await calendarApiFetch('/tasks')
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) {
        throw new Error((payload as { detail?: string }).detail ?? 'Kunne ikke hente oppgaver')
      }

      const mappedTasks = ((payload as { tasks?: Record<string, unknown>[] }).tasks ?? []).map(mapTaskRow)
      setTasks(mappedTasks)
      setError(null)
      await fetchExecutions(mappedTasks.map(task => task.id))
      await fetchWorkflowHistory(mappedTasks.map(task => task.id))
      await fetchWorkflowTimeline(mappedTasks.map(task => task.id))
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }, [fetchExecutions, fetchWorkflowHistory, fetchWorkflowTimeline, userId])

  useEffect(() => {
    void fetchTasks()
  }, [fetchTasks])

  useEffect(() => {
    if (!userId) return

    const hasRunning = Object.values(executionsByTask).some(executions =>
      executions.some(execution => execution.status === 'pending' || execution.status === 'running')
    )
    if (!hasRunning) return

    const intervalId = window.setInterval(() => {
      void fetchExecutions()
    }, 5000)

    return () => window.clearInterval(intervalId)
  }, [executionsByTask, fetchExecutions, userId])

  const createTask = useCallback(async (
    proposal: RecurringTaskProposal,
    source: 'agent' | 'manual' = 'agent',
    autoApproved = false,
  ): Promise<RecurringTask | null> => {
    if (!userId) return null
    const normalizedWorkflowSpec = normalizeWorkflowSpec(proposal, proposal.workflowSpec)
    const color = proposal.color || categoryColor(proposal.category)
    const res = await calendarApiFetch('/tasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: proposal.title,
        description: proposal.description,
        category: proposal.category,
        color,
        frequency: proposal.frequency,
        custom_days: proposal.customDays ?? null,
        day_of_week: proposal.dayOfWeek ?? null,
        day_of_month: proposal.dayOfMonth ?? null,
        time_of_day: proposal.timeOfDay,
        connector_id: proposal.connectorId ?? null,
        connector_name: proposal.connectorName ?? null,
        agent_prompt: encodeAgentPrompt(proposal.agentPrompt, normalizedWorkflowSpec),
        ends_at: proposal.endsAt ?? null,
        is_active: true,
        auto_approved: autoApproved,
        source,
      }),
    })
    const payload = await res.json().catch(() => ({}))

    if (!res.ok) {
      setError((payload as { detail?: string }).detail ?? 'Kunne ikke opprette oppgaven')
      return null
    }

    const task = mapTaskRow((payload as { task?: Record<string, unknown> }).task ?? {})
    setTasks(prev => [task, ...prev])
    setExecutionsByTask(prev => ({ ...prev, [task.id]: [] }))
    setError(null)
    return task
  }, [userId])

  const updateTask = useCallback(async (
    taskId: string,
    patch: Partial<RecurringTaskProposal> & { isActive?: boolean; autoApproved?: boolean },
  ): Promise<RecurringTask | null> => {
    const existingTask = tasks.find(task => task.id === taskId)
    if (!existingTask) return null

    const merged: RecurringTask = {
      ...existingTask,
      ...patch,
      workflowSpec: normalizeWorkflowSpec(
        {
          description: patch.description ?? existingTask.description,
          connectorName: patch.connectorName ?? existingTask.connectorName,
          frequency: patch.frequency ?? existingTask.frequency,
          customDays: patch.customDays ?? existingTask.customDays,
          dayOfWeek: patch.dayOfWeek ?? existingTask.dayOfWeek,
          dayOfMonth: patch.dayOfMonth ?? existingTask.dayOfMonth,
          timeOfDay: patch.timeOfDay ?? existingTask.timeOfDay,
        },
        patch.workflowSpec ?? existingTask.workflowSpec,
      ),
    }

    const updatePayload = {
      title: merged.title,
      description: merged.description,
      category: merged.category,
      color: merged.color,
      frequency: merged.frequency,
      custom_days: merged.customDays ?? null,
      day_of_week: merged.dayOfWeek ?? null,
      day_of_month: merged.dayOfMonth ?? null,
      time_of_day: merged.timeOfDay,
      connector_id: merged.connectorId ?? null,
      connector_name: merged.connectorName ?? null,
      agent_prompt: encodeAgentPrompt(merged.agentPrompt, merged.workflowSpec),
      ends_at: merged.endsAt ?? null,
      is_active: patch.isActive ?? merged.isActive,
      auto_approved: patch.autoApproved ?? merged.autoApproved,
    }

    const res = await calendarApiFetch(`/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePayload),
    })
    const payload = await res.json().catch(() => ({}))

    if (!res.ok) {
      setError((payload as { detail?: string }).detail ?? 'Kunne ikke oppdatere oppgaven')
      return null
    }

    const updatedTask = mapTaskRow((payload as { task?: Record<string, unknown> }).task ?? {})
    setTasks(prev => prev.map(task => task.id === taskId ? updatedTask : task))
    setError(null)
    return updatedTask
  }, [tasks])

  const toggleActive = useCallback(async (taskId: string, active: boolean) => {
    const res = await calendarApiFetch(`/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: active }),
    })
    if (res.ok) {
      setTasks(prev => prev.map(task => task.id === taskId ? { ...task, isActive: active } : task))
      setError(null)
      return
    }

    const payload = await res.json().catch(() => ({}))
    setError((payload as { detail?: string }).detail ?? 'Kunne ikke oppdatere status')
  }, [])

  const deleteTask = useCallback(async (taskId: string) => {
    const res = await calendarApiFetch(`/tasks/${taskId}`, {
      method: 'DELETE',
    })
    if (res.ok) {
      setTasks(prev => prev.filter(task => task.id !== taskId))
      setExecutionsByTask(prev => {
        const next = { ...prev }
        delete next[taskId]
        return next
      })
      setError(null)
      return
    }

    const payload = await res.json().catch(() => ({}))
    setError((payload as { detail?: string }).detail ?? 'Kunne ikke slette oppgaven')
  }, [])

  const runTaskNow = useCallback(async (taskId: string): Promise<TaskRunResponse | null> => {
    const res = await apiFetch(`${API_BASE}/calendar/tasks/${taskId}/run`, {
      method: 'POST',
    })
    const payload = await res.json().catch(() => ({}))

    if (!res.ok) {
      const message = (payload as { detail?: string }).detail ?? 'Kunne ikke starte oppgaven'
      setError(message)
      return null
    }

    const executionId = (payload as { execution_id?: string | null }).execution_id ?? null
    const taskTitle = (payload as { task_title?: string }).task_title ?? 'Oppgave'
    const runMode = (payload as { run_mode?: 'safe' | 'power' }).run_mode ?? 'safe'
    const startedAt = new Date().toISOString()

    if (executionId) {
      setExecutionsByTask(prev => ({
        ...prev,
        [taskId]: [
          {
            id: executionId,
            taskId,
            scheduledFor: startedAt,
            startedAt,
            status: 'running',
            creditsUsed: 0,
            triggeredBy: 'manual',
            createdAt: startedAt,
          },
          ...(prev[taskId] ?? []),
        ],
      }))
    }

    setError(null)
    void fetchExecutions([taskId])
    void fetchWorkflowHistory([taskId])
    void fetchWorkflowTimeline([taskId])

    return {
      executionId,
      taskTitle,
      runMode,
    }
  }, [fetchExecutions, fetchWorkflowHistory, fetchWorkflowTimeline])

  return {
    tasks,
    executionsByTask,
    workflowHistoryByTask,
    workflowTimelineByTask,
    loading,
    error,
    fetchTasks,
    fetchExecutions,
    fetchWorkflowHistory,
    fetchWorkflowTimeline,
    createTask,
    updateTask,
    toggleActive,
    deleteTask,
    runTaskNow,
  }
}
